import { useCallback } from 'react';

import { getPath, pathToUrl, routeNames } from '../../../shared/helpers/routes';
import {
  trackButtonClickEvent,
  trackClickEvent,
} from '../../../services/tracking/pulseEvents';
import { getCurrentPageViewData } from '../../../services/tracking/helpers/pulseOrigin';
import { useAlgoliaAnalytics } from '../../AlgoliaAnalytics/AlgoliaAnalytics';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

export const useAssetItemTracking = ({
  elementType,
  header = '',
  headerButtonLabel,
  pageComponentIndex,
  skin = 'vertical',
}: {
  elementType: string;
  header?: string;
  headerButtonLabel?: string;
  pageComponentIndex?: number;
  skin?: 'vertical' | 'horizontal';
}) => {
  const { trackHitClick } = useAlgoliaAnalytics();

  const trackAssetClick = useCallback(
    (asset: SvpAssetAdditional, columnIndex: number) => {
      trackHitClick({ objectID: String(asset.id) });

      const parent = {
        elementType,
        header,
        row: pageComponentIndex,
        skin,
      };

      const url = pathToUrl(
        getPath(routeNames.asset, {
          asset,
          skipDeeplinkCheck: true,
        })
      );

      const object = {
        id: asset.id,
        title: asset.title,
        elementType: 'Asset',
        position: columnIndex,
        url: url,
      };

      const target = {
        id: asset.id,
        title: asset.title,
        type: 'Video',
        url: url,
      };

      trackClickEvent(parent, object, target);
    },
    [trackHitClick, elementType, header, pageComponentIndex, skin]
  );

  const trackHeaderButtonClick = useCallback(() => {
    if (!headerButtonLabel) {
      return;
    }

    const currentPageViewData = getCurrentPageViewData();

    const parent = {
      elementType,
      name: header,
      row: pageComponentIndex,
    };

    const target = {
      elementType: 'ComponentPopup',
      name: header,
      url: currentPageViewData.url,
    };

    const button = {
      elementType: 'SeeAll',
      title: headerButtonLabel,
      position: 'slider-header',
    };

    trackButtonClickEvent(button, target, parent);
  }, [elementType, header, pageComponentIndex, headerButtonLabel]);

  return {
    trackAssetClick,
    trackHeaderButtonClick,
  };
};
